import _ from 'lodash'
import React from 'react'
import { Link } from 'react-router'

import { APP_MARKET_PLACE } from 'constants/colors'
import { Nursery } from 'services/nurseries/models'
import { STRIPE_STATUSES } from 'services/nurseryIntegrations/constants'

import { Button, Flex, Page, Spinner, Toolbar, Typography } from 'components'

import i18n from 'translations'

import { MarketPlaceProps } from '../AppMarketplaceConstants'
import {
  StyledContentContainer,
  StyledDetailImage,
  StyledHeaderContainer,
  StyledKeyFeaturesListContainer,
  StyledLogo,
  StyledLogoContainer,
  StyledSubTitleImage,
} from './DetailStyled'
import { StyledChip } from '../components/AppMarketplaceCardStyled'

interface ExternAppViewProps {
  appMarketplaceData: MarketPlaceProps
  isBePaidFetching?: boolean
  mobilePaymentsIsEnabled: boolean
  nurseryData?: Nursery
  onBePaidConnectClick: () => void
  onBePaidManageClick: () => void
}

const DetailView: React.FC<ExternAppViewProps> = ({
  appMarketplaceData,
  isBePaidFetching,
  mobilePaymentsIsEnabled,
  nurseryData,
  onBePaidConnectClick,
  onBePaidManageClick,
}) => {
  const isBePaidConnected = nurseryData?.stripeNurseryIntegration?.status === STRIPE_STATUSES.COMPLETED
    || nurseryData?.stripeNurseryIntegration?.status === STRIPE_STATUSES.ENABLED
    || nurseryData?.stripeNurseryIntegration?.status === STRIPE_STATUSES.RESTRICTED_SOON

  let showOfferText = true

  if ('be-paid' === appMarketplaceData.appType && isBePaidConnected) {
    showOfferText = false
  }

  const renderActions = () => {
    if ('be-paid' === appMarketplaceData.appType) {
      if (!mobilePaymentsIsEnabled) {
        return (
          <Toolbar.Group>
            <Toolbar.Item>
              <Button
                hierarchy="tertiary"
                label={i18n.t('module:AppMarketplace:Common:learnMore')}
                target="_blank"
                to={appMarketplaceData.detail.learnMoreLink}
                negativeMargins
              />
            </Toolbar.Item>
          </Toolbar.Group>
        )
      }

      if (isBePaidFetching) {
        return <Spinner size="small" />
      }

      if (isBePaidConnected) {
        return (
          <Toolbar.Group>
            <Toolbar.Item>
              <StyledChip $color={APP_MARKET_PLACE.CONNECTED_TEXT}>
                <Typography>{i18n.t('module:AppMarketplace:Status:connected')}</Typography>
              </StyledChip>
            </Toolbar.Item>
            <Toolbar.Item>
              <Button
                hierarchy="tertiary"
                label={i18n.t('module:AppMarketplace:Common:manage')}
                negativeMargins
                onClick={onBePaidManageClick}
              />
            </Toolbar.Item>
          </Toolbar.Group>
        )
      }

      return (
        <Toolbar.Group>
          <Toolbar.Item>
            <Button
              label={i18n.t('module:AppMarketplace:Common:connect')}
              negativeMargins
              onClick={onBePaidConnectClick}
            />
          </Toolbar.Item>
          <Toolbar.Item>
            <Button
              hierarchy="tertiary"
              label={i18n.t('module:AppMarketplace:Common:learnMore')}
              target="_blank"
              to={appMarketplaceData.detail.learnMoreLink}
              negativeMargins
            />
          </Toolbar.Item>
        </Toolbar.Group>
      )
    }

    return (
      <Toolbar.Group>
        {appMarketplaceData.detail.externalLink && (
          <Toolbar.Item>
            <Button
              label={i18n.t('module:AppMarketplace:Common:openApp')}
              target="_blank"
              to={appMarketplaceData.detail.externalLink}
              negativeMargins
            />
          </Toolbar.Item>
        )}
        <Toolbar.Item>
          <Button
            hierarchy="tertiary"
            label={i18n.t('module:AppMarketplace:Common:learnMore')}
            target="_blank"
            to={appMarketplaceData.detail.learnMoreLink}
            negativeMargins
          />
        </Toolbar.Item>
      </Toolbar.Group>
    )
  }

  return (
    <Page.Section>
      <StyledHeaderContainer>
        <StyledLogoContainer>
          <StyledLogo src={appMarketplaceData.appLogo} />
          <Typography fontSize={22} bold>
            {appMarketplaceData.appName}
          </Typography>
        </StyledLogoContainer>
        <Toolbar noMargin>
          {renderActions()}
        </Toolbar>
      </StyledHeaderContainer>
      <StyledContentContainer>
        <Flex flexDirection="column" gap="24px">
          <div>
            <Flex alignItems="center" gap="10px">
              <StyledSubTitleImage src={appMarketplaceData.detail.subTitleImage} />
              <Typography color={appMarketplaceData.titleColor} fontSize={16} fontWeight={500}>
                {appMarketplaceData.title}
              </Typography>
            </Flex>
            <Typography fontSize={20} margin="8px 0 0" bold>
              {appMarketplaceData.description}
            </Typography>
          </div>
          <div>
            <Typography fontSize={18} bold>
              {i18n.t('module:AppMarketplace:Common:keyFeatures')}
            </Typography>
            <StyledKeyFeaturesListContainer>
              {_.map(appMarketplaceData.detail.keyFeatures, (feature, index) => <li key={index}>{feature}</li>)}
            </StyledKeyFeaturesListContainer>
          </div>
          {appMarketplaceData.detail.integrationText && (
            <div>
              <Typography fontSize={18} margin="0 0 8px" bold>
                {i18n.t('module:AppMarketplace:Common:integration')}
              </Typography>
              <Typography fontSize={16} width="auto">{appMarketplaceData.detail.integrationText}</Typography>
            </div>
          )}
          {showOfferText && !isBePaidFetching && (
            <div>
              <Typography fontSize={18} margin="0 0 8px" bold>
                {i18n.t('module:AppMarketplace:Common:dontHaveAccount')}
              </Typography>
              <StyledChip
                $backgroundColor={APP_MARKET_PLACE.SPECIAL_OFFER}
                $color={APP_MARKET_PLACE.SPECIAL_OFFER_TEXT}
                as={Link}
                target="_blank"
                to={appMarketplaceData.detail.learnMoreLink}
              >
                <Typography fontSize={16}>
                  {appMarketplaceData?.detail?.specialOfferDetailText
                    || i18n.t('module:AppMarketplace:Common:signUpNow')}
                </Typography>
              </StyledChip>
            </div>
          )}
        </Flex>
        <StyledDetailImage src={appMarketplaceData.detail.detailImage} />
      </StyledContentContainer>
    </Page.Section>
  )
}

export default DetailView
